/**
 * Run-as-timeline + the inline sign-off card (Agents Phase 4, design §5–§6).
 *
 * A run renders as a calm vertical timeline of curated milestones — what triggered
 * it → what it read → what it decided → what it did / is waiting on — each expandable
 * inline. When the run is suspended awaiting sign-off, an INLINE SIGN-OFF CARD appears
 * in the timeline showing the EXACT prepared action (the real draft / structured args),
 * provenance flags, and Approve / Edit / Reject. Decisions post through the existing
 * decide path (`useDecideApproval`) — record-only, never sends/executes (design §5.3).
 *
 * This is a reframing of the queue ApprovalCard (views.tsx) for the run context: same
 * structured decision (`approved | edited | rejected` + optional `preparedPayload`),
 * same 6s local-undo window, but it lives in the run's conversation, not a managed queue.
 */
import { useEffect, useState } from "react";
import type {
  AgentRunDetail,
  MilestoneKind,
  RunMilestone,
  RunSignOff,
} from "./fixtures-deep";
import { ReversibilityTag } from "./badges";
import { useDecideApproval } from "./useAgentsUi";

const UNDO_SECS = 6;

const MILESTONE_GLYPH: Record<MilestoneKind, string> = {
  trigger: "⚡",
  read: "👁",
  decided: "✎",
  did: "✓",
  waiting: "⏳",
};
const MILESTONE_LABEL: Record<MilestoneKind, string> = {
  trigger: "Triggered",
  read: "Read",
  decided: "Decided",
  did: "Did",
  waiting: "Waiting",
};

/**
 * The run-as-timeline. Renders curated milestones (or falls back to the run's
 * read/decided/did audit fields when a run has no curated milestones), then the
 * inline sign-off card when the run is awaiting sign-off.
 */
export function RunTimeline({ run }: { run: AgentRunDetail }) {
  const milestones = run.milestones ?? deriveMilestones(run);
  return (
    <div className="agui-timeline">
      <ol className="agui-timeline__list">
        {milestones.map((m) => (
          <TimelineBeat key={m.id} milestone={m} />
        ))}
      </ol>
      {run.awaitingSignOff ? (
        <InlineSignOffCard signOff={run.awaitingSignOff} />
      ) : null}
    </div>
  );
}

function TimelineBeat({ milestone: m }: { milestone: RunMilestone }) {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(m.detail || m.detailList?.length);
  const headId = `${m.id}-head`;
  const panelId = `${m.id}-panel`;

  return (
    <li className={`agui-tl agui-tl--${m.kind}${m.kind === "waiting" ? " is-waiting" : ""}`}>
      <span className="agui-tl__rail" aria-hidden="true">
        <span className="agui-tl__node">{MILESTONE_GLYPH[m.kind]}</span>
      </span>
      <div className="agui-tl__body">
        {hasDetail ? (
          <button
            type="button"
            id={headId}
            className="agui-tl__head agui-tl__head--btn"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((v) => !v)}
          >
            <BeatHead milestone={m} />
            <span className="agui-tl__chevron" aria-hidden="true">
              {open ? "▾" : "▸"}
            </span>
          </button>
        ) : (
          <div className="agui-tl__head">
            <BeatHead milestone={m} />
          </div>
        )}
        {open && hasDetail ? (
          <div className="agui-tl__detail" id={panelId} role="region" aria-labelledby={headId}>
            {m.detail ? <p className="agui-tl__detailtext">{m.detail}</p> : null}
            {m.detailList?.length ? (
              <ul className="agui-tl__detaillist">
                {m.detailList.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            ) : null}
            {m.objectLabel ? (
              <span className="agui-objlink">→ {m.objectLabel}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function BeatHead({ milestone: m }: { milestone: RunMilestone }) {
  return (
    <span className="agui-tl__headrow">
      <span className="agui-tl__kind">{MILESTONE_LABEL[m.kind]}</span>
      <span className="agui-tl__label">{m.label}</span>
      {m.reversibility ? <ReversibilityTag kind={m.reversibility} /> : null}
      {m.when ? <span className="agui-tl__when">{m.when}</span> : null}
    </span>
  );
}

/** Fallback timeline for a run with no curated milestones — built from its audit fields. */
function deriveMilestones(run: AgentRunDetail): RunMilestone[] {
  const beats: RunMilestone[] = [
    { id: `${run.id}-t`, kind: "trigger", label: run.trigger, when: run.when },
  ];
  if (run.read.length) {
    beats.push({
      id: `${run.id}-r`,
      kind: "read",
      label: `Read ${run.read.length} source${run.read.length > 1 ? "s" : ""}`,
      detailList: run.read,
    });
  }
  beats.push({ id: `${run.id}-d`, kind: "decided", label: run.decided });
  for (const [i, action] of run.did.entries()) {
    beats.push({
      id: `${run.id}-did-${i}`,
      kind: action.reversibility === "sign-off" ? "waiting" : "did",
      label: action.label,
      reversibility: action.reversibility,
      objectLabel: action.objectLabel,
      objectType: action.objectType,
    });
  }
  return beats;
}

/**
 * The inline sign-off card. Renders the prepared action in the run's timeline and
 * decides it through the existing path. Approve / Reject post the structured decision;
 * Edit opens an inline editor on the prepared payload and approves the EDITED payload
 * (`decision: "edited"`, `preparedPayload`). A 6s local-undo holds before committing.
 */
/** camelCase / snake_case key → readable label, e.g. "threadId" → "Thread id". */
function prettyKey(k: string): string {
  const s = k
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Render a prepared-action value readably — primitives as-is, objects compactly. */
function prettyVal(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function InlineSignOffCard({ signOff }: { signOff: RunSignOff }) {
  const decide = useDecideApproval();
  const email = signOff.email;
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(email?.body ?? "");
  const [grant, setGrant] = useState(false);
  const [pending, setPending] = useState<{
    decision: "approved" | "edited";
    secs: number;
  } | null>(null);

  // Undo window: hold the decision locally and only commit after the countdown.
  useEffect(() => {
    if (!pending) return;
    if (pending.secs <= 0) {
      decide.mutate({
        id: signOff.approvalId,
        decision: pending.decision,
        // On "edited", carry the user's edits so they persist (not just the status).
        preparedPayload:
          pending.decision === "edited" && email
            ? { to: email.to, subject: email.subject, body }
            : undefined,
      });
      return;
    }
    const t = setTimeout(
      () => setPending((p) => (p ? { ...p, secs: p.secs - 1 } : p)),
      1000,
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const recipientFlag = signOff.provenanceFlags.find(
    (f) => f.param === "recipient",
  );

  if (pending) {
    return (
      <div className="agui-signoff agui-signoff--done" role="status">
        <span className="agui-signoff__check" aria-hidden="true">
          ✓
        </span>
        <span>
          {pending.decision === "edited"
            ? "Approved with your edits"
            : "Approved"}{" "}
          — recording in {pending.secs}s
        </span>
        <button
          type="button"
          className="agui-link"
          onClick={() => setPending(null)}
        >
          Undo
        </button>
      </div>
    );
  }

  return (
    <div className="agui-signoff" role="group" aria-label="Sign-off required">
      <div className="agui-signoff__head">
        <span className="agui-signoff__title">Your sign-off needed</span>
        <span className="agui-chip agui-tag agui-tag--signoff">
          {signOff.actionType}
        </span>
      </div>

      {email ? (
        <div className="agui-signoff__email">
          <div className="agui-signoff__row">
            <span className="agui-signoff__k">To</span>
            <span>
              {email.to}
              {recipientFlag ? (
                <span className="agui-prov">⚠ {recipientFlag.note}</span>
              ) : null}
            </span>
          </div>
          <div className="agui-signoff__row">
            <span className="agui-signoff__k">Subject</span>
            <span>{email.subject}</span>
          </div>
          {editing ? (
            <textarea
              className="agui-signoff__bodyedit"
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              aria-label="Email body"
            />
          ) : (
            <div className="agui-signoff__body">{body}</div>
          )}
          {email.sendTime ? (
            <div className="agui-signoff__when">
              Send time: {email.sendTime}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="agui-signoff__args">
          <div className="agui-signoff__argshead">Prepared action</div>
          {Object.entries(signOff.args ?? {}).length > 0 ? (
            Object.entries(signOff.args ?? {}).map(([k, v]) => (
              <div key={k} className="agui-signoff__row">
                <span className="agui-signoff__k">{prettyKey(k)}</span>
                <span>{prettyVal(v)}</span>
              </div>
            ))
          ) : (
            <div className="agui-signoff__body">No further details.</div>
          )}
        </div>
      )}

      {signOff.provenanceFlags.length > 1 ||
      (signOff.provenanceFlags.length === 1 && !recipientFlag) ? (
        <ul className="agui-signoff__flags">
          {signOff.provenanceFlags
            .filter((f) => f !== recipientFlag)
            .map((f) => (
              <li key={f.param} className="agui-prov">
                ⚠ {f.param}: {f.note}
              </li>
            ))}
        </ul>
      ) : null}

      {signOff.grantOffer ? (
        <label className="agui-signoff__grant">
          <input
            type="checkbox"
            checked={grant}
            onChange={() => setGrant((g) => !g)}
          />
          <span>{signOff.grantOffer}</span>
        </label>
      ) : null}

      <div className="agui-signoff__actions">
        {editing ? (
          <>
            <button
              type="button"
              className="agui-btn agui-btn--primary"
              onClick={() => {
                setEditing(false);
                setPending({ decision: "edited", secs: UNDO_SECS });
              }}
            >
              Save &amp; approve
            </button>
            <button
              type="button"
              className="agui-btn"
              onClick={() => {
                setEditing(false);
                setBody(email?.body ?? "");
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="agui-btn agui-btn--primary"
              onClick={() =>
                setPending({ decision: "approved", secs: UNDO_SECS })
              }
            >
              Approve
            </button>
            {email ? (
              <button
                type="button"
                className="agui-btn"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            ) : null}
            <button
              type="button"
              className="agui-btn agui-btn--danger"
              onClick={() =>
                decide.mutate({
                  id: signOff.approvalId,
                  decision: "rejected",
                })
              }
            >
              Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}
